/* eslint-disable @next/next/no-img-element */
"use client";

import { motion, Variants } from "framer-motion";
import React from "react";

export const useHeroSection = ({
  title,
  subtitle,
  buttonText,
  backgroundImage,
  buttonLink,
  gradientFrom = "#4177BC",
  gradientVia = "#4177BC",
  gradientTo = "transparent",
}: {
  title?: string;
  subtitle?: string;
  buttonText?: string;
  backgroundImage?: string;
  buttonLink?: string;
  gradientFrom?: string;
  gradientVia?: string;
  gradientTo?: string;
}) => {
  // 🔹 Animation variants
  const fadeUp: Variants = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: "easeOut" },
    },
  };

  // 🔹 Safely handle missing fields
  const hasContent = title || subtitle || buttonText;

  const HeroSection = hasContent ? (
    <section className="relative w-full min-h-[400px] md:h-[650px] flex items-center overflow-hidden">
      {/* ✅ Background Image (optional) */}
      {backgroundImage && (
        <div className="absolute inset-0">
          <img
            src={backgroundImage}
            alt="Hero Background"
            className="absolute inset-0 w-full h-full object-cover object-[right_center] md:object-[75%_center]"
          />
        </div>
      )}

      {/* ✅ Gradient Overlay (optional) */}
      {(gradientFrom || gradientVia || gradientTo) && (
        <div
          className="absolute inset-0 transition-all duration-500"
          style={{
            backgroundImage: `linear-gradient(to right, ${gradientFrom}90, ${gradientVia}66, ${gradientTo})`,
          }}
        ></div>
      )}

      {/* ✅ Content */}
      <motion.div
        variants={fadeUp}
        initial="hidden"
        animate="visible"
        className="relative z-10 w-full max-w-[1315px] mx-auto px-4 sm:px-6 md:px-12 flex items-center"
      >
        <div className="text-white space-y-6 max-w-[800px] text-center md:text-left">
          {/* Title */}
          {title && (
            <motion.h1
              variants={fadeUp}
              className="text-[38px] sm:text-[50px] md:text-[60px] lg:text-[80px] judson-regular leading-tight"
            >
              {title}
            </motion.h1>
          )}

          {/* Subtitle */}
          {subtitle && (
            <motion.p
              variants={fadeUp}
              transition={{ delay: 0.2 }}
              className="text-sm sm:text-base md:text-lg text-gray-100 inter-medium mx-auto md:mx-0 max-w-[620px]"
            >
              {subtitle}
            </motion.p>
          )}

          {/* Button */}
          {buttonText && (
            <motion.div
              whileHover={{ scale: 1.05 }}
              transition={{ type: "spring", stiffness: 200 }}
              className="flex justify-center md:justify-start cursor-pointer"
            >
              <a href={buttonLink || "#"}>
                <button className="bg-[#EB9C2C] cursor-pointer hover:bg-white hover:text-black text-white px-6 py-3 rounded-lg font-semibold inter-semibold transition">
                  {buttonText}
                </button>
              </a>
            </motion.div>
          )}
        </div>
      </motion.div>
    </section>
  ) : null; // ✅ return nothing if no content

  return HeroSection;
};
